'use client'

import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { api, apiHeaders } from '../configs/api'
import { useAuth } from './AuthContext'

export type ProfessionalDataType = {
    id: number
    name: string
    image: string | null
    email: string
    admin: boolean
    status: boolean
}

export type ProfessionalDetailType = {
    professional: ProfessionalDataType
    notes: {
        id: number
        businessID: number
        title: string
        name: string
        date: string
        dateNotify: string | null
        description: string
        users: { id: number; name: string; image?: string }[]
        status: 0 | 1
        isExpired: boolean
    }[]
    business: {
        id: number
        pipeline: string
        step: string
        color: string
        description: string | null
        products: { id: number; name: string }[]
        targets: { id: number; name: string; color: string }[]
    }[]
}

type ProfessionalType = {
    loading: boolean
    professionals: ProfessionalDataType[]
    setProfessionals: React.Dispatch<React.SetStateAction<ProfessionalDataType[]>>
    filteredProfessionals: ProfessionalDataType[]
    setFilteredProfessionals: React.Dispatch<React.SetStateAction<ProfessionalDataType[]>>
    loadProfessionals: () => Promise<void>
    getProfessional: (id: string | number) => Promise<void>
    detail: ProfessionalDetailType | null
    setDetail: React.Dispatch<React.SetStateAction<ProfessionalDetailType | null>>
}

const Professional = createContext<ProfessionalType | undefined>(undefined)

export const ProfessionalProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { unity, token } = useAuth()
    const [loading, setLoading] = useState(false)
    const [professionals, setProfessionals] = useState<ProfessionalDataType[]>([])
    const [filteredProfessionals, setFilteredProfessionals] = useState<ProfessionalDataType[]>([])
    const [detail, setDetail] = useState<ProfessionalDetailType | null>(null)

    const loadProfessionals = useCallback(async () => {
        if (!unity || !token) return

        setLoading(true)
        try {
            const response = await fetch(`${api}professional/getData`, {
                method: 'POST',
                headers: apiHeaders(token),
                body: JSON.stringify({ unityID: unity.unityID })
            })
            const result = await response.json()
            if (!response.ok || !result.success) throw new Error(result.message)
            const list = (result.list || []) as ProfessionalDataType[]
            setProfessionals(list)
            setFilteredProfessionals(list)
        } catch (error) {
            setProfessionals([])
            setFilteredProfessionals([])
            toast.error(error instanceof Error && error.message ? error.message : 'Erro ao carregar profissionais!')
        } finally {
            setLoading(false)
        }
    }, [token, unity])

    const getProfessional = useCallback(
        async (id: string | number) => {
            if (!unity || !token) return

            try {
                const response = await fetch(`${api}professional/getDetail`, {
                    method: 'POST',
                    headers: apiHeaders(token),
                    body: JSON.stringify({ id, unityID: unity.unityID })
                })
                const result = await response.json()
                if (!response.ok || !result.success) throw new Error(result.message)
                setDetail(result.detail)
            } catch (error) {
                setDetail(null)
                toast.error(error instanceof Error && error.message ? error.message : 'Erro ao carregar profissional!')
            }
        },
        [token, unity]
    )

    useEffect(() => {
        loadProfessionals()
    }, [loadProfessionals])

    return (
        <Professional.Provider
            value={{
                loading,
                professionals,
                setProfessionals,
                filteredProfessionals,
                setFilteredProfessionals,
                loadProfessionals,
                getProfessional,
                detail,
                setDetail
            }}
        >
            {children}
        </Professional.Provider>
    )
}

export function useProfessional() {
    const context = useContext(Professional)
    if (context === undefined) throw new Error('useProfessional must be used within a ProfessionalProvider')
    return context
}
